// THE ORG DISPLAY the shell chrome renders — fetched ONCE at shell mount from this box's own config.
//
// WHY THIS EXISTS. The org name and its meta line used to be constants compiled into the client bundle.
// Every demo instance serves the SAME release tree, so a constant brands every prospect's box as the demo
// tenant, "Orange Grove Ltd". The name has to be a per-Deployment setting read at RUNTIME (owner direction,
// 7 Aug 2026), which means the shell has to ask the server for it.
//
// WHY A CONTEXT RATHER THAN A FETCH PER RENDER SITE. Two places paint the org block — the persistent
// Sidebar (visible on every surface, for every role) and the Work module's dev HarnessRail, which
// deliberately mirrors the sidebar's identity block. They must never disagree, and the value must not be
// re-fetched once per mount. One read at the shell root, published through a context, gives both the same
// answer from the same request. It is also the only shell-ROOT read in the app: every other read belongs to
// a Surface and is fetched by that Surface.
//
// WHY THE FALLBACK IS NOT `null`. `useOrgDisplay` NEVER returns an empty or absent value. It starts at
// `ORG_DISPLAY` — the same two strings the config schema defaults to — so the first paint is correct on an
// un-reconfigured box, a slow read shows the right chrome rather than a blank corner, and a failed read
// degrades to the default instead of an empty sidebar. A read that resolves simply replaces it. A component
// rendered OUTSIDE the provider (an isolated component test) gets the same fallback rather than throwing:
// unlike the transport contexts, missing org chrome is a cosmetic default, never a wiring bug worth a crash.

import { createContext, useContext, useEffect, useState } from 'react';
import { ORG_DISPLAY, type OrgDisplay } from './shell-identity.ts';
import { useSurfaceApi } from '../surfaces/SurfaceApiContext.tsx';

const OrgDisplayContext = createContext<OrgDisplay>(ORG_DISPLAY);

/** The org identity for the shell chrome. Always a usable value — the built-in default until the read lands. */
export function useOrgDisplay(): OrgDisplay {
  return useContext(OrgDisplayContext);
}

/**
 * Fetch this Deployment's org display once and publish it to the chrome. Mounted at the shell root, inside
 * the SurfaceApi provider so the read goes through the SAME injected transport every surface uses — a UI
 * test therefore never touches the network for it either (the hermetic-test constraint the transport exists
 * to hold). A rejected read is SWALLOWED deliberately: the only consequence is that the chrome keeps the
 * default strings, which is exactly the intended degradation, and a toast about branding would be noise.
 */
export function OrgDisplayProvider({
  children,
}: {
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const api = useSurfaceApi();
  const [org, setOrg] = useState<OrgDisplay>(ORG_DISPLAY);
  useEffect(() => {
    let live = true;
    void api
      .getOrgDisplay()
      .then((next) => {
        // Guard the unmounted case AND an empty answer: a box that configured the name to '' would
        // otherwise paint a blank corner, which is the one outcome this whole seam exists to avoid.
        if (live && next.name.length > 0) setOrg({ name: next.name, meta: next.meta });
      })
      .catch(() => {
        /* the read failed — the chrome keeps the built-in default (never a blank org block) */
      });
    return () => {
      live = false;
    };
  }, [api]);
  return <OrgDisplayContext.Provider value={org}>{children}</OrgDisplayContext.Provider>;
}
